import { Link, useLocation } from "react-router-dom";
import FlagStripe from '@/components/FlagStripe';
import { Home, Search, Activity, TrendingUp, FileText, Settings, Shield, ChevronDown, Database, ShoppingBag, Users2, Bell, Globe, MessageCircle, MoreHorizontal, X, Star, Map as MapIcon, Globe2, Heart, HeartHandshake, BookOpen, Gift, FlaskConical, Code2, Plug, MapPin, Scale } from "lucide-react";
import { useAdminStatus } from "@/hooks/useAdminStatus";
import { useAppReviewFlags } from "@/config/appReviewFlags";
import { useAuth } from "@/hooks/useAuth";
import { useBrand } from "@/config/brands";
import { useTheme } from "@/contexts/ThemeContext";
import { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";

export const AppFooter = () => {
  const location = useLocation();
  const brand = useBrand();
  const { isDark } = useTheme();
  const { t } = useTranslation();
  const { data: adminStatus } = useAdminStatus();
  const isAdmin = adminStatus?.isAdmin;
  const { isAuthenticated } = useAuth();
  // App-review gates: merch stays hidden until the remote flag flips it public
  // (admins always see it for QA); "What's New" hides on native during review.
  const reviewFlags = useAppReviewFlags();
  const showMerch = isAdmin || reviewFlags.showMerch;
  const [selectedCategory, setSelectedCategory] = useState('hospitals');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [hasInteracted, setHasInteracted] = useState(false);
  const [animationCount, setAnimationCount] = useState(0);
  const [isMobileOrTablet, setIsMobileOrTablet] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);
  const moreRef = useRef<HTMLDivElement>(null);

  const isActive = (path: string) => location.pathname === path;

  // Full "All sections" list for the desktop More sheet — everything not already
  // a primary tab in the footer row. Mirrors the mobile NativeTabBar More sheet.
  const moreItems: Array<{ to: string; label: string; Icon: typeof Home }> = [
    { to: "/near-me", label: "Near Me", Icon: MapPin },
    { to: "/followed", label: "Followed", Icon: Star },
    { to: "/map", label: "Map", Icon: MapIcon },
    { to: "/international", label: "International", Icon: Globe2 },
    { to: "/connect-to-care", label: "Connect to Care", Icon: Heart },
    { to: "/about/dignity", label: "Dignity", Icon: HeartHandshake },
    ...((reviewFlags.showStories || isAdmin) ? [{ to: "/stories", label: "Stories", Icon: BookOpen }] : []),
    { to: "/data-sources", label: "Data Sources", Icon: Database },
    { to: "/data-methodology", label: "Methodology", Icon: FlaskConical },
    { to: "/api", label: "API", Icon: Code2 },
    { to: "/mcp", label: "MCP", Icon: Plug },
    { to: "/lawsuits", label: "Legal Record", Icon: Scale },
    ...(reviewFlags.showReleaseNotes ? [{ to: "/release-notes", label: "What's New", Icon: FileText }] : []),
    { to: "/donate", label: "Support", Icon: Gift },
    { to: "/settings", label: t('nav.settings'), Icon: Settings },
    { to: "/chat", label: "Connect AI", Icon: MessageCircle },
    ...(showMerch ? [{ to: "/merch", label: t('nav.merch'), Icon: ShoppingBag }] : []),
  ];

  // Close the More sheet on outside click / route change.
  useEffect(() => {
    if (!moreOpen) return;
    const onDown = (e: MouseEvent) => { if (moreRef.current && !moreRef.current.contains(e.target as Node)) setMoreOpen(false); };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [moreOpen]);
  useEffect(() => { setMoreOpen(false); }, [location.pathname]);

  // Detect mobile/tablet for footer padding
  useEffect(() => {
    const checkMobileOrTablet = () => {
      setIsMobileOrTablet(window.innerWidth < 1024); // lg breakpoint
    };

    checkMobileOrTablet();
    window.addEventListener('resize', checkMobileOrTablet);

    return () => window.removeEventListener('resize', checkMobileOrTablet);
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Subtle scroll animation to indicate carousel is movable (mobile only)
  useEffect(() => {
    // Respect the user's reduced-motion preference — skip the attract animation.
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    const isMobile = window.innerWidth < 768; // md breakpoint
    if (!isMobile || hasInteracted || animationCount >= 3) return;

    const animateScroll = () => {
      const container = scrollContainerRef.current;
      if (!container) return;

      const startScroll = container.scrollLeft;
      const scrollAmount = 100; // pixels to scroll right
      const duration = 800; // animation duration in ms
      const startTime = performance.now();

      const animate = (currentTime: number) => {
        const elapsed = currentTime - startTime;
        const progress = Math.min(elapsed / duration, 1);

        // Ease-in-out function for smooth animation
        const easeInOut = progress < 0.5
          ? 2 * progress * progress
          : 1 - Math.pow(-2 * progress + 2, 2) / 2;

        if (progress < 0.5) {
          // Scroll right
          container.scrollLeft = startScroll + scrollAmount * easeInOut * 2;
        } else {
          // Scroll back left
          container.scrollLeft = startScroll + scrollAmount * (1 - (easeInOut - 0.5) * 2);
        }

        if (progress < 1) {
          requestAnimationFrame(animate);
        } else {
          setAnimationCount(prev => prev + 1);
        }
      };

      requestAnimationFrame(animate);
    };

    const timer = setTimeout(animateScroll, 2000); // Start after 2 seconds
    const interval = setInterval(animateScroll, 5000); // Repeat every 5 seconds

    return () => {
      clearTimeout(timer);
      clearInterval(interval);
    };
  }, [hasInteracted, animationCount]);

  // Detect user interaction to stop animation
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const handleInteraction = () => setHasInteracted(true);

    container.addEventListener('touchstart', handleInteraction);
    container.addEventListener('scroll', handleInteraction);

    return () => {
      container.removeEventListener('touchstart', handleInteraction);
      container.removeEventListener('scroll', handleInteraction);
    };
  }, []);

  // Label padding — the safe-area clearance itself comes from the env()-based
  // paddingBottom on the nav container below (replaces the old HoS_iOS UA sniff).
  const textPadding = 'pb-1';

  // Categories for the dropdown (only hospitals enabled for now)
  const categories = [
    { value: 'hospitals', label: 'Hospitals', enabled: true },
    { value: 'insurance', label: 'Insurance Plans', enabled: false },
    { value: 'gas', label: 'Gas', enabled: false },
    { value: 'taxes', label: 'Taxes', enabled: false },
    { value: 'fastfood', label: 'Fast Food', enabled: false },
    { value: 'utilities', label: 'Utilities', enabled: false },
    { value: 'telecom', label: 'Telecom', enabled: false }
  ];

  // AHP brand-specific nav styles
  const isAhp = brand.key === 'ahp';

  // AHP dark mode: navy bottom nav with gold accents; light mode: patriotic red
  const ahpNavBg = isAhp
    ? isDark
      ? { background: '#0a1628', borderTop: '2px solid rgba(197,165,90,0.25)' }
      : { background: '#B22234', borderTop: '2px solid rgba(255,255,255,0.15)' }
    : null;
  const navBg = brand.key === 'antigreed'
    ? { background: 'linear-gradient(1deg, #092018, #258c6a)' }
    : isAhp
    ? ahpNavBg!
    : { background: 'hsl(var(--background) / 0.95)' };

  // Active/inactive link colors adapt to dark mode for AHP
  const activeClass = isAhp
    ? isDark ? 'text-[#C5A55A]' : 'text-white'
    : 'text-matrix-green';
  const inactiveClass = isAhp
    ? isDark ? 'text-white/45 hover:text-white/80' : 'text-white/60 hover:text-white'
    : 'text-foreground hover:text-matrix-green';
  const labelFont = isAhp ? 'font-sans' : 'font-mono';

  // Gradient fade color for scroll indicator matches nav bg
  const ahpFadeColor = isAhp ? (isDark ? '#0a1628' : '#B22234') : '#B22234';

  // Skip footer when embedded in an iframe (for in-modal previews)
  if (typeof window !== "undefined" && new URLSearchParams(window.location.search).get("embed") === "1") {
    return null;
  }

  return (
    <nav
      className={`fixed bottom-0 left-0 right-0 z-[10000] ${isAhp ? '' : 'backdrop-blur-sm border-t border-matrix-green/20'} ${brand.key === 'luigis' ? 'matrix-pulse-footer' : ''}`}
      style={{ ...navBg, paddingBottom: 'max(env(safe-area-inset-bottom, 0px), 4px)' }}
    >
      {/* Matrix pulse effect - only for Luigi's brand */}
      {brand.key === 'luigis' && (
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="matrix-pulse-sweep" />
        </div>
      )}
      {/* Gradient fade indicators on mobile — color-matched to nav background */}
      {isAhp ? (
        <>
          <div className="absolute left-0 top-0 bottom-0 w-8 pointer-events-none z-10 md:hidden" style={{ background: `linear-gradient(to right, ${ahpFadeColor}, transparent)` }} />
          <div className="absolute right-0 top-0 bottom-0 w-8 pointer-events-none z-10 md:hidden" style={{ background: `linear-gradient(to left, ${ahpFadeColor}, transparent)` }} />
        </>
      ) : (
        <>
          <div className="absolute left-0 top-0 bottom-0 w-8 bg-gradient-to-r from-background/80 to-transparent pointer-events-none z-10 md:hidden" />
          <div className="absolute right-0 top-0 bottom-0 w-8 bg-gradient-to-l from-background/80 to-transparent pointer-events-none z-10 md:hidden" />
        </>
      )}

      <div className={`max-w-4xl mx-auto ${isMobileOrTablet ? 'h-20' : 'h-16'} flex items-center gap-4`} style={isMobileOrTablet ? { paddingTop: '16px', paddingBottom: '15px' } : {}}>
        {/* Navigation buttons - Swipeable on mobile, centered on desktop */}
        <div
          ref={scrollContainerRef}
          className="flex-1 flex md:justify-around items-center overflow-x-auto scrollbar-hide snap-x snap-mandatory scroll-smooth px-2 md:px-4"
          style={{
            WebkitOverflowScrolling: 'touch',
            scrollbarWidth: 'none',
            msOverflowStyle: 'none'
          }}
        >
          <Link
            to="/"
            className={`flex flex-col items-center gap-1 pt-2 transition-colors px-4 md:px-2 md:mx-3 lg:mx-4 snap-start flex-shrink-0 ${
              isActive('/') ? activeClass : inactiveClass
            }`}
          >
            <Home className="w-7 h-7 md:w-6 md:h-6" />
            <span className={`text-xs ${labelFont} whitespace-nowrap ${textPadding}`}>{t('nav.home')}</span>
          </Link>

          <Link
            to="/search"
            className={`flex flex-col items-center gap-1 pt-2 transition-colors px-4 md:px-2 md:mx-3 lg:mx-4 snap-start flex-shrink-0 ${
              isActive('/search') ? activeClass : inactiveClass
            }`}
          >
            <Search className="w-7 h-7 md:w-6 md:h-6" />
            <span className={`text-xs ${labelFont} whitespace-nowrap ${textPadding}`}>{t('nav.search')}</span>
          </Link>

          <Link
            to="/scenarios"
            className={`flex flex-col items-center gap-1 pt-2 transition-colors px-4 md:px-2 md:mx-3 lg:mx-4 snap-start flex-shrink-0 ${
              location.pathname.startsWith('/scenarios') ? activeClass : inactiveClass
            }`}
          >
            <Activity className="w-7 h-7 md:w-6 md:h-6" />
            <span className={`text-xs ${labelFont} whitespace-nowrap ${textPadding}`}>{t('nav.scenarios')}</span>
          </Link>

          <Link
            to="/family"
            className={`flex flex-col items-center gap-1 pt-2 transition-colors px-4 md:px-2 md:mx-3 lg:mx-4 snap-start flex-shrink-0 ${
              location.pathname.startsWith('/family') ? activeClass : inactiveClass
            }`}
          >
            <Users2 className="w-7 h-7 md:w-6 md:h-6" />
            <span className={`text-xs ${labelFont} whitespace-nowrap ${textPadding}`}>{t('nav.family')}</span>
          </Link>

          <Link
            to="/hospital-rankings"
            className={`flex flex-col items-center gap-1 pt-2 transition-colors px-4 md:px-2 md:mx-3 lg:mx-4 snap-start flex-shrink-0 ${
              location.pathname.startsWith('/hospital-rankings') ? activeClass : inactiveClass
            }`}
          >
            <TrendingUp className="w-7 h-7 md:w-6 md:h-6" />
            <span className={`text-xs ${labelFont} whitespace-nowrap ${textPadding}`}>{t('nav.ranks')}</span>
          </Link>

          <Link
            to="/state-comparison"
            className={`flex flex-col items-center gap-1 pt-2 transition-colors px-4 md:px-2 md:mx-3 lg:mx-4 snap-start flex-shrink-0 ${
              location.pathname.startsWith('/state-comparison') ? activeClass : inactiveClass
            }`}
          >
            <Globe className="w-7 h-7 md:w-6 md:h-6" />
            <span className={`text-xs ${labelFont} whitespace-nowrap ${textPadding}`}>States</span>
          </Link>

          <Link
            to="/follow"
            className={`flex flex-col items-center gap-1 pt-2 transition-colors px-4 md:px-2 md:mx-3 lg:mx-4 snap-start flex-shrink-0 ${
              location.pathname.startsWith('/follow') ? activeClass : inactiveClass
            }`}
          >
            <Bell className="w-7 h-7 md:w-6 md:h-6" />
            <span className={`text-xs ${labelFont} whitespace-nowrap ${textPadding}`}>{t('nav.follow')}</span>
          </Link>

          <Link
            to="/chat"
            className={`flex flex-col items-center gap-1 pt-2 transition-colors px-4 md:px-2 md:mx-3 lg:mx-4 snap-start flex-shrink-0 ${
              location.pathname.startsWith('/chat') ? activeClass : inactiveClass
            }`}
          >
            <MessageCircle className="w-7 h-7 md:w-6 md:h-6" />
            <span className={`text-xs ${labelFont} whitespace-nowrap ${textPadding}`}>Connect AI</span>
          </Link>

          {/* Stories link hidden for now */}

          {showMerch && (
            <Link
              to="/merch"
              className={`flex flex-col items-center gap-1 pt-2 transition-colors px-4 md:px-2 md:mx-3 lg:mx-4 snap-start flex-shrink-0 ${
                location.pathname.startsWith('/merch') ? activeClass : inactiveClass
              }`}
            >
              <ShoppingBag className="w-7 h-7 md:w-6 md:h-6" />
              <span className={`text-xs ${labelFont} whitespace-nowrap ${textPadding}`}>{t('nav.merch')}</span>
            </Link>
          )}

          <Link
            to="/settings"
            className={`flex flex-col items-center gap-1 pt-2 transition-colors px-4 md:px-2 md:mx-3 lg:mx-4 snap-start flex-shrink-0 ${
              location.pathname.startsWith('/settings') ? activeClass : inactiveClass
            }`}
          >
            <Settings className="w-7 h-7 md:w-6 md:h-6" />
            <span className={`text-xs ${labelFont} whitespace-nowrap ${textPadding}`}>{t('nav.settings')}</span>
          </Link>

        </div>

        {/* More — desktop "all sections" sheet (mirrors the mobile tab bar's More).
            Sits outside the scroll container so the popover isn't clipped. */}
        <div className="relative flex-shrink-0 hidden md:block" ref={moreRef}>
          <button
            onClick={() => setMoreOpen((o) => !o)}
            aria-label="More sections"
            aria-expanded={moreOpen}
            className={`flex flex-col items-center gap-1 pt-2 px-2 md:mx-3 lg:mx-4 transition-colors ${moreOpen ? activeClass : inactiveClass}`}
          >
            <MoreHorizontal className="w-7 h-7 md:w-6 md:h-6" />
            <span className={`text-xs ${labelFont} whitespace-nowrap ${textPadding}`}>More</span>
          </button>

          {moreOpen && (
            <div
              className="absolute bottom-full right-0 mb-3 rounded-2xl shadow-2xl overflow-hidden"
              style={{
                width: 460, maxWidth: '90vw',
                background: isAhp ? (isDark ? '#0a1628' : '#ffffff') : '#0a0f0a',
                border: `1px solid ${isAhp ? (isDark ? 'rgba(197,165,90,0.25)' : 'rgba(0,40,104,0.15)') : 'rgba(0,255,3,0.25)'}`,
              }}
            >
              {/* flag stripe + header */}
              <FlagStripe height={4} />
              <div className="flex items-center justify-between px-4 pt-3 pb-1">
                <span style={{ fontFamily: "'Instrument Serif', Georgia, serif", fontSize: 20, color: isAhp ? (isDark ? '#fff' : '#002868') : '#00ff03' }}>
                  All sections
                </span>
                <button onClick={() => setMoreOpen(false)} aria-label="Close" className="p-1 rounded" style={{ color: isDark ? 'rgba(255,255,255,0.5)' : 'rgba(27,27,27,0.62)' }}>
                  <X className="w-4 h-4" />
                </button>
              </div>
              <div className="grid grid-cols-4 gap-1 p-3">
                {moreItems.map(({ to, label, Icon }) => {
                  const active = location.pathname === to || (to !== '/' && location.pathname.startsWith(to));
                  const accent = isAhp ? (isDark ? '#C5A55A' : '#B22234') : '#00ff03';
                  return (
                    <Link
                      key={to}
                      to={to}
                      onClick={() => setMoreOpen(false)}
                      className="flex flex-col items-center gap-1.5 py-3 px-1 rounded-xl transition-colors"
                      style={{
                        color: active ? accent : (isAhp ? (isDark ? 'rgba(240,235,227,0.8)' : '#1B1B1B') : 'rgba(0,255,3,0.8)'),
                        background: active ? (isAhp ? (isDark ? 'rgba(197,165,90,0.12)' : 'rgba(178,34,52,0.08)') : 'rgba(0,255,3,0.1)') : 'transparent',
                      }}
                    >
                      <Icon className="w-6 h-6" />
                      <span className="text-[11px] font-medium text-center leading-tight" style={{ fontFamily: isAhp ? "'Instrument Sans', system-ui, sans-serif" : undefined }}>{label}</span>
                    </Link>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        {/* Category Dropdown - Only show for AntiGreed brand */}
        {brand.key === 'antigreed' && (
          <div className="relative" ref={dropdownRef}>
            <button
              onClick={() => setIsDropdownOpen(!isDropdownOpen)}
              className="flex items-center gap-2 px-3 py-2 bg-background/80 border border-matrix-green/30 rounded-md hover:bg-background/90 transition-colors font-mono text-sm"
            >
              <span className="text-foreground">
                {categories.find(c => c.value === selectedCategory)?.label}
              </span>
              <ChevronDown className={`w-4 h-4 text-foreground/70 transition-transform ${isDropdownOpen ? 'rotate-180' : ''}`} />
            </button>

            {/* Dropdown Menu */}
            {isDropdownOpen && (
              <div className="absolute bottom-full right-0 mb-2 w-48 bg-background border border-matrix-green/30 rounded-md shadow-lg overflow-hidden">
                {categories.map((category) => (
                  <button
                    key={category.value}
                    onClick={() => {
                      if (category.enabled) {
                        setSelectedCategory(category.value);
                        setIsDropdownOpen(false);
                      }
                    }}
                    disabled={!category.enabled}
                    className={`w-full px-4 py-2 text-left font-mono text-sm transition-colors ${
                      category.enabled
                        ? 'hover:bg-matrix-green/10 text-foreground cursor-pointer'
                        : 'text-foreground/30 cursor-not-allowed'
                    } ${
                      selectedCategory === category.value ? 'bg-matrix-green/20' : ''
                    }`}
                  >
                    {category.label}
                    {!category.enabled && <span className="ml-2 text-xs">(Coming Soon)</span>}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
      {/* Build version tag — desktop only, bottom-right */}
      <div
        className="hidden lg:block fixed bottom-1 right-2 select-none pointer-events-none"
        style={{ zIndex: 9, fontFamily: "'JetBrains Mono', 'Fira Code', monospace", fontSize: 9, letterSpacing: '0.5px', color: isDark ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.1)' }}
      >
        v0.0.9-pre · build {import.meta.env.VITE_BUILD_ID || 'dev'}
      </div>
    </nav>
  );
};
